import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom';
import { FaHeartbeat, FaShieldAlt, FaClock, FaArrowRight } from 'react-icons/fa';
import pana from "../assets/pana.png";

export default function PreDashboard() {
    const navigate = useNavigate();
    const [countdown, setCountdown] = useState(15);
    const hospitalName = JSON.parse(localStorage.getItem("hospital_name"));

    useEffect(() => {
        const timer = setTimeout(() => {
            navigate("/homedashboard");
        }, 15000);

        // Countdown timer
        const countdownTimer = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    clearInterval(countdownTimer);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => {
            clearTimeout(timer);
            clearInterval(countdownTimer);
        };
    }, [navigate]);

    const handleProceedNow = () => {
        navigate("/homedashboard");
    };

    return (
        <div className="h-screen flex flex-col bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
            {/* Header Section with Gradient - Full Width, Touches Top */}
            <div className="bg-gradient-to-r from-primary100 to-emerald-600 px-6 py-8 sm:py-12 text-center relative overflow-hidden flex-shrink-0">
                {/* Decorative Elements */}
                <div className="absolute top-0 left-0 w-full h-full opacity-10">
                    <div className="absolute top-4 left-4 w-8 h-8 bg-white rounded-full animate-pulse"></div>
                    <div className="absolute top-6 right-6 w-4 h-4 bg-white rounded-full animate-pulse delay-100"></div>
                    <div className="absolute bottom-4 left-6 w-6 h-6 bg-white rounded-full animate-pulse delay-200"></div>
                    <div className="absolute bottom-6 right-4 w-3 h-3 bg-white rounded-full animate-pulse delay-300"></div>
                    <div className="absolute top-1/2 left-1/4 w-2 h-2 bg-white rounded-full animate-pulse delay-500"></div>
                    <div className="absolute top-1/3 right-1/3 w-5 h-5 bg-white rounded-full animate-pulse delay-700"></div>
                </div>
                
                {/* Logo Section */}
                <div className="flex justify-center mb-6 relative z-10">
                    <div className="w-24 h-24 sm:w-28 sm:h-28 bg-white rounded-full p-4 shadow-2xl transform transition-transform duration-300 hover:rotate-6 hover:scale-110">
                        <img 
                            src={pana} 
                            alt="Healthcare Portal" 
                            className="w-full h-full object-contain"
                        />
                    </div>
                </div>
                
                {/* Welcome Text */}
                <h1 className="text-white text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 relative z-10 tracking-wide">
                    Welcome to
                </h1>
                <h2 className="text-white text-xl sm:text-2xl lg:text-3xl font-semibold mb-4 relative z-10">
                    {hospitalName || "Promise Patient App"}
                </h2>
                <div className="w-20 h-1 bg-white mx-auto rounded-full relative z-10 shadow-lg"></div>
            </div>

            {/* Main Content Section - Flexible Height */}
            <div className="flex-1 flex flex-col justify-center px-4 py-6">
                <div className="w-full max-w-md sm:max-w-lg lg:max-w-xl mx-auto bg-white rounded-3xl shadow-2xl overflow-hidden transform transition-all duration-500 hover:scale-105">
                    
                    {/* Content Section */}
                    <div className="px-6 py-8 space-y-6">
                        {/* Welcome Message */}
                        <div className="text-center">
                            <p className="text-gray-700 text-base sm:text-lg leading-relaxed font-medium">
                                Your health is our priority. We're here to provide quality care and support your journey to better health.
                            </p>
                        </div>

                        {/* Feature Highlights - Enhanced */}
                        <div className="flex justify-center items-center space-x-8 py-4">
                            <div className="flex flex-col items-center text-center group">
                                <div className="w-16 h-16 bg-gradient-to-br from-green-50 to-green-100 rounded-full flex items-center justify-center mb-3 shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-110">
                                    <FaHeartbeat className="text-primary100 text-xl" />
                                </div>
                                <span className="text-sm font-semibold text-gray-700">Quality Care</span>
                            </div>
                            <div className="flex flex-col items-center text-center group">
                                <div className="w-16 h-16 bg-gradient-to-br from-blue-50 to-blue-100 rounded-full flex items-center justify-center mb-3 shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-110">
                                    <FaShieldAlt className="text-blue-600 text-xl" />
                                </div>
                                <span className="text-sm font-semibold text-gray-700">Secure Portal</span>
                            </div>
                            <div className="flex flex-col items-center text-center group">
                                <div className="w-16 h-16 bg-gradient-to-br from-purple-50 to-purple-100 rounded-full flex items-center justify-center mb-3 shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-110">
                                    <FaClock className="text-purple-600 text-xl" />
                                </div>
                                <span className="text-sm font-semibold text-gray-700">24/7 Access</span>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="space-y-4">
                            <button
                                onClick={handleProceedNow}
                                className="w-full bg-gradient-to-r from-primary100 to-emerald-600 text-white py-5 px-6 rounded-2xl font-bold text-lg shadow-xl hover:shadow-2xl transform transition-all duration-300 hover:scale-105 active:scale-95 flex items-center justify-center space-x-3 hover:from-emerald-600 hover:to-primary100"
                            >
                                <span>Proceed to Dashboard</span>
                                <FaArrowRight className="text-lg animate-pulse" />
                            </button>
                            
                            {/* Auto-redirect Notice */}
                            <div className="text-center">
                                <p className="text-gray-500 text-sm font-medium">
                                    Auto-redirecting in{' '}
                                    <span className="inline-flex items-center justify-center w-8 h-8 bg-gradient-to-r from-primary100 to-emerald-600 text-white rounded-full text-sm font-bold mx-1 shadow-lg animate-pulse">
                                        {countdown}
                                    </span>
                                    {countdown === 1 ? 'sec' : 'secs'}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="px-6 pb-6">
                        <div className="w-full bg-gray-200 rounded-full h-3 shadow-inner">
                            <div 
                                className="bg-gradient-to-r from-primary100 to-emerald-600 h-3 rounded-full transition-all duration-1000 ease-linear shadow-lg"
                                style={{ width: `${((15 - countdown) / 15) * 100}%` }}
                            ></div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer - Fixed at Bottom */}
            <div className="flex-shrink-0 text-center py-4 bg-white/50 backdrop-blur-sm">
                <p className="text-gray-600 text-sm font-medium">
                    Powered by Misas EMR.
                </p>
            </div>
        </div>
    );
}
